import { useState, useEffect } from "react";
import { useAuth, API } from "@/App";
import axios from "axios";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { 
  Download, 
  Monitor, 
  Apple, 
  Chrome,
  CheckCircle2,
  Cpu,
  HardDrive,
  Wifi,
  RefreshCw,
  Info,
  ExternalLink,
  FileDown,
  Package
} from "lucide-react";
import {
  Alert,
  AlertDescription,
  AlertTitle,
} from "@/components/ui/alert";

const formatFileSize = (bytes) => {
  if (bytes === 0) return '0 Bytes';
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i];
};

const DownloadApp = () => {
  const { getAuthHeaders } = useAuth();
  const [downloads, setDownloads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [version, setVersion] = useState("1.0.0");
  const [downloading, setDownloading] = useState(null);
  
  // Detect user's platform
  const detectPlatform = () => {
    const userAgent = navigator.userAgent.toLowerCase();
    if (userAgent.includes('win')) return 'windows';
    if (userAgent.includes('mac')) return 'mac';
    if (userAgent.includes('linux')) return 'linux';
    return 'unknown';
  };
  
  const currentPlatform = detectPlatform();

  useEffect(() => {
    fetchDownloads();
  }, []);

  const fetchDownloads = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/downloads/available`, getAuthHeaders());
      setDownloads(response.data.downloads || []);
      setVersion(response.data.version || "1.0.0");
    } catch (error) {
      console.error("Error fetching downloads:", error);
      toast.error("Errore nel caricamento download disponibili");
    } finally {
      setLoading(false);
    }
  };

  const handleDownload = async (download) => {
    setDownloading(download.name);
    try {
      // Create a direct download link
      const downloadUrl = `${API}${download.path}`;
      
      // Create invisible anchor element for direct download
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.download = download.name;
      link.style.display = 'none';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      toast.success(`Download avviato: ${download.name}`);
    } catch (error) {
      console.error('Download error:', error);
      toast.error("Errore durante il download");
    } finally {
      setTimeout(() => setDownloading(null), 2000);
    }
  };
  
  const platforms = {
    linux: {
      name: 'Linux',
      icon: Chrome,
      color: '#FCC624',
      requirements: 'Ubuntu 18.04+, Debian 10+, o equivalente'
    },
    windows: {
      name: 'Windows',
      icon: Monitor,
      color: '#0078D4',
      requirements: 'Windows 10 o superiore (64-bit)'
    },
    mac: {
      name: 'macOS',
      icon: Apple,
      color: '#000000',
      requirements: 'macOS 10.15 (Catalina) o superiore'
    }
  };

  const features = [
    { icon: HardDrive, title: 'Prestazioni Native', desc: 'App desktop veloce e reattiva' },
    { icon: Cpu, title: 'Integrazione Sistema', desc: 'Notifiche e scorciatoie' },
    { icon: Wifi, title: 'Sync Cloud', desc: 'Dati sempre sincronizzati' }
  ];

  // Group downloads by platform
  const groupedDownloads = downloads.reduce((acc, d) => {
    if (!acc[d.platform]) acc[d.platform] = [];
    acc[d.platform].push(d);
    return acc;
  }, {});

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <RefreshCw className="w-8 h-8 animate-spin text-[#007AFF]" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="download-page">
      {/* Header */}
      <div className="text-center max-w-2xl mx-auto">
        <div className="w-20 h-20 mx-auto bg-gradient-to-br from-[#007AFF] to-[#5856D6] rounded-2xl flex items-center justify-center mb-4 shadow-lg">
          <Package className="w-10 h-10 text-white" />
        </div>
        <h1 className="text-3xl font-bold text-[#1D1D1F]">Scarica Treasury Dashboard</h1>
        <p className="text-[#86868B] mt-2">
          Versione {version} - App desktop per la gestione completa della tesoreria
        </p>
      </div>

      {/* Downloads Available */}
      {downloads.length > 0 ? (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {Object.entries(groupedDownloads).map(([platform, platformDownloads]) => {
            const platformInfo = platforms[platform] || { name: platform, icon: Download, color: '#666' };
            const Icon = platformInfo.icon;
            const isCurrentPlatform = currentPlatform === platform;
            
            return (
              <Card 
                key={platform}
                className={`bg-white rounded-2xl border-0 shadow-[0_2px_12px_rgba(0,0,0,0.04)] ${
                  isCurrentPlatform ? 'ring-2 ring-[#34C759]' : ''
                }`}
              >
                <CardHeader className="text-center pb-2">
                  <div 
                    className="w-16 h-16 mx-auto rounded-2xl flex items-center justify-center mb-3"
                    style={{ backgroundColor: `${platformInfo.color}15` }}
                  >
                    <Icon className="w-8 h-8" style={{ color: platformInfo.color }} />
                  </div>
                  <CardTitle className="text-xl font-semibold text-[#1D1D1F]">
                    {platformInfo.name}
                  </CardTitle>
                  {isCurrentPlatform && (
                    <Badge className="bg-[#34C759] text-white mt-1">
                      <CheckCircle2 className="w-3 h-3 mr-1" />
                      Il tuo sistema
                    </Badge>
                  )}
                  <CardDescription className="text-xs text-[#86868B] mt-2">
                    {platformInfo.requirements}
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-2">
                  {platformDownloads.map((download, idx) => (
                    <Button
                      key={idx}
                      variant={idx === 0 ? "default" : "outline"}
                      className={`w-full justify-between ${idx === 0 ? 'bg-[#007AFF] hover:bg-[#0056b3]' : ''}`}
                      onClick={() => handleDownload(download)}
                      disabled={downloading === download.name}
                    >
                      <span className="flex items-center gap-2">
                        {downloading === download.name ? (
                          <RefreshCw className="w-4 h-4 animate-spin" />
                        ) : (
                          <FileDown className="w-4 h-4" />
                        )}
                        {download.type}
                      </span>
                      <span className="text-xs opacity-70">{formatFileSize(download.size)}</span>
                    </Button>
                  ))}
                </CardContent>
              </Card>
            );
          })}
          
          {/* Show placeholder for platforms without downloads */}
          {['windows', 'mac'].filter(p => !groupedDownloads[p]).map((platform) => {
            const platformInfo = platforms[platform];
            const Icon = platformInfo.icon;
            
            return (
              <Card 
                key={platform}
                className="bg-gray-50 rounded-2xl border-0 opacity-60"
              >
                <CardHeader className="text-center pb-2">
                  <div 
                    className="w-16 h-16 mx-auto rounded-2xl flex items-center justify-center mb-3 bg-gray-200"
                  >
                    <Icon className="w-8 h-8 text-gray-400" />
                  </div>
                  <CardTitle className="text-xl font-semibold text-gray-400">
                    {platformInfo.name}
                  </CardTitle>
                  <CardDescription className="text-xs text-gray-400 mt-2">
                    Build in preparazione...
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <Button variant="outline" className="w-full" disabled>
                    Presto disponibile
                  </Button>
                </CardContent>
              </Card>
            );
          })}
        </div>
      ) : (
        <Alert className="bg-amber-50 border-amber-200">
          <Info className="h-4 w-4 text-amber-600" />
          <AlertTitle className="text-amber-800">Download in preparazione</AlertTitle>
          <AlertDescription className="text-amber-700">
            I pacchetti di installazione sono in fase di build. Riprova tra qualche minuto.
          </AlertDescription>
        </Alert>
      )}

      {/* Features */}
      <Card className="bg-gradient-to-br from-[#007AFF]/5 to-[#5856D6]/5 rounded-2xl border-0">
        <CardHeader>
          <CardTitle className="text-lg font-semibold text-[#1D1D1F]">
            Vantaggi dell'app desktop
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {features.map((feature, idx) => {
              const Icon = feature.icon;
              return (
                <div key={idx} className="flex items-start gap-3 p-4 rounded-xl bg-white/50">
                  <div className="w-10 h-10 rounded-lg bg-[#007AFF]/10 flex items-center justify-center flex-shrink-0">
                    <Icon className="w-5 h-5 text-[#007AFF]" />
                  </div>
                  <div>
                    <h4 className="font-medium text-[#1D1D1F]">{feature.title}</h4>
                    <p className="text-sm text-[#86868B]">{feature.desc}</p>
                  </div>
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>

      {/* Installation Instructions */}
      <Card className="bg-white rounded-2xl border-0 shadow-[0_2px_12px_rgba(0,0,0,0.04)]">
        <CardHeader>
          <CardTitle className="text-lg font-semibold text-[#1D1D1F]">
            Istruzioni di installazione
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {/* Windows */}
            <div className="space-y-2">
              <h4 className="font-medium text-[#1D1D1F] flex items-center gap-2">
                <Monitor className="w-4 h-4" style={{ color: '#0078D4' }} />
                Windows
              </h4>
              <ol className="text-sm text-[#86868B] space-y-1 list-decimal list-inside">
                <li>Scarica il file .zip</li>
                <li>Estrai in una cartella</li>
                <li>Esegui "Treasury Dashboard.exe"</li>
                <li>Crea collegamento sul desktop (opzionale)</li>
              </ol>
            </div>
            
            {/* macOS */}
            <div className="space-y-2">
              <h4 className="font-medium text-[#1D1D1F] flex items-center gap-2">
                <Apple className="w-4 h-4" />
                macOS
              </h4>
              <ol className="text-sm text-[#86868B] space-y-1 list-decimal list-inside">
                <li>Scarica il file .zip</li>
                <li>Estrai il contenuto</li>
                <li>Sposta l'app in Applicazioni</li>
                <li>Ctrl+click → Apri (primo avvio)</li>
              </ol>
            </div>
            
            {/* Linux */}
            <div className="space-y-2">
              <h4 className="font-medium text-[#1D1D1F] flex items-center gap-2">
                <Chrome className="w-4 h-4" style={{ color: '#FCC624' }} />
                Linux
              </h4>
              <ol className="text-sm text-[#86868B] space-y-1 list-decimal list-inside">
                <li>Scarica .AppImage</li>
                <li>chmod +x *.AppImage</li>
                <li>Doppio click per eseguire</li>
                <li>Oppure: ./TreasuryDashboard.AppImage</li>
              </ol>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Info Alert */}
      <Alert className="bg-blue-50 border-blue-200">
        <Info className="h-4 w-4 text-blue-600" />
        <AlertTitle className="text-blue-800">Connessione richiesta</AlertTitle>
        <AlertDescription className="text-blue-700 text-sm">
          L'app desktop si connette al server cloud per sincronizzare i dati. 
          È necessaria una connessione internet attiva per utilizzare l'applicazione.
        </AlertDescription>
      </Alert>

      {/* Version Info */}
      <div className="text-center text-sm text-[#86868B]">
        <p>Treasury Dashboard v{version}</p>
        <p className="text-xs mt-1">
          Costruito con Electron • React • FastAPI • MongoDB
        </p>
      </div>
    </div>
  );
};

export default DownloadApp;
